import { useState } from 'react';
import type { Me } from '@hangar/shared';
import { canChangePassword } from '@/auth/changePassword';
import { roleLabel } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { ThemeMenuItems } from '@/components/ThemeSwitch';
import { UserAvatar } from '@/components/UserAvatar';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { identityOf } from './shellModel';

function ProfileError({ rail }: { rail: boolean }) {
  return (
    <Alert variant="destructive" className={cn('p-2 text-xs', rail && 'justify-items-center')} title="Could not load your profile">
      <AlertTitle className="col-start-1 col-end-3 flex items-center gap-2 text-xs">
        <Icon name="error" size={18} />
        {!rail ? <span>Profile unavailable</span> : null}
      </AlertTitle>
    </Alert>
  );
}

function ProfileSkeleton({ rail }: { rail: boolean }) {
  return (
    <div className={cn('flex items-center gap-2.5 rounded-lg border bg-secondary p-2', rail && 'justify-center')}>
      <Skeleton className="size-9 rounded-full" />
      {!rail ? (
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : null}
    </div>
  );
}

function AccountMenuItems({ onSignOut, onChangePassword }: { onSignOut: () => void; onChangePassword?: () => void }) {
  return (
    <>
      <ThemeMenuItems />
      <DropdownMenuSeparator />
      {onChangePassword ? (
        <DropdownMenuItem onSelect={onChangePassword} data-testid="change-password-open">
          Change password
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem onSelect={onSignOut} data-testid="sign-out">
        Sign out
      </DropdownMenuItem>
    </>
  );
}

/** Tablet rail: the avatar alone, the name and role in the menu. */
function RailUserCard({ me, onSignOut, onChangePassword }: { me: Me; onSignOut: () => void; onChangePassword?: () => void }) {
  const { name, email } = identityOf(me);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-lg" title={`${name} · ${email}`} aria-label="Account menu">
          <UserAvatar name={name} email={email} size={36} />
          <span data-testid="me-email" className="sr-only">
            {email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent aria-label="Account" side="right" align="end" sideOffset={8}>
        <div className="border-b px-2.5 pb-2 pt-1.5">
          <div className="truncate text-sm font-medium text-foreground">{name}</div>
          <div className="truncate text-xs text-subtle">
            {roleLabel(me)} · <span className="font-mono">{email}</span>
          </div>
        </div>
        <AccountMenuItems onSignOut={onSignOut} onChangePassword={onChangePassword} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FullUserCard({ me, onSignOut, onChangePassword }: { me: Me; onSignOut: () => void; onChangePassword?: () => void }) {
  const { name, email } = identityOf(me);
  return (
    <div className="flex items-center gap-2.5 rounded-lg border bg-secondary p-2">
      <UserAvatar name={name} email={email} size={36} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{name}</div>
        <div className="truncate text-xs text-subtle">
          {roleLabel(me)} ·{' '}
          <span data-testid="me-email" className="font-mono" title={email}>
            {email}
          </span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Account menu" className="text-subtle hover:bg-surface-3">
            <Icon name="more_vert" size={20} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent aria-label="Account" side="top" align="end">
          <AccountMenuItems onSignOut={onSignOut} onChangePassword={onChangePassword} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function UserCard({ me, meError, rail, onSignOut }: { me: Me | undefined; meError: boolean; rail: boolean; onSignOut: () => void }) {
  const [changing, setChanging] = useState(false);
  if (!me) return meError ? <ProfileError rail={rail} /> : <ProfileSkeleton rail={rail} />;
  // Tokens and a client preview have no password of the viewer's to change; the dev issuer has no passwords at all.
  const onChangePassword = canChangePassword() && me.user && !me.preview ? () => setChanging(true) : undefined;
  return (
    <>
      {rail ? <RailUserCard me={me} onSignOut={onSignOut} onChangePassword={onChangePassword} /> : <FullUserCard me={me} onSignOut={onSignOut} onChangePassword={onChangePassword} />}
      <ChangePasswordDialog open={changing} onOpenChange={setChanging} />
    </>
  );
}
